import { createContext, ReactNode, useContext, useState } from 'react';
import { Transition } from '@headlessui/react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id: string;
  type: ToastType;
  message: string;
}

interface ToastContextValue {
  toast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within ToastProvider');
  return context;
};

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const toast = (message: string, type: ToastType = 'info') => {
    const id = Math.random().toString(36).substring(7);
    setToasts((prev) => [...prev, { id, type, message }]);

    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 5000);
  };

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  const iconMap = {
    success: CheckCircleIcon,
    error: XCircleIcon,
    warning: ExclamationTriangleIcon,
    info: InformationCircleIcon,
  };

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <div className="pointer-events-none fixed right-4 top-4 z-50 flex flex-col gap-2">
        {toasts.map((t) => {
          const Icon = iconMap[t.type];
          return (
            <Transition
              key={t.id}
              show={true}
              enter="transition-all duration-300"
              enterFrom="opacity-0 translate-x-4"
              enterTo="opacity-100 translate-x-0"
              leave="transition-all duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div
                className={clsx(
                  'pointer-events-auto flex items-start gap-3 rounded-lg p-4 shadow-lg',
                  {
                    'bg-green-50 text-green-800': t.type === 'success',
                    'bg-red-50 text-red-800': t.type === 'error',
                    'bg-yellow-50 text-yellow-800': t.type === 'warning',
                    'bg-blue-50 text-blue-800': t.type === 'info',
                  }
                )}
              >
                <Icon className="h-5 w-5 flex-shrink-0" />
                <p className="flex-1 text-s">{t.message}</p>
                <button
                  onClick={() => removeToast(t.id)}
                  className="flex-shrink-0 opacity-70 hover:opacity-100"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </Transition>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};
